import authService from './AuthService';

interface Subscriber {
  resolve: (token: string) => void;
  reject: (err: unknown) => void;
}

let isRefreshing = false;
let subscribers: Subscriber[] = [];

interface StoredUserInfo {
  token?: string;
  exp?: number;
}

// Returns true if the stored userinfo's token has already expired or will
// expire within bufferMs milliseconds (default 30s). Returns false when
// userinfo is missing, not parseable, or has no exp field. `exp` is already
// stored as a ms-epoch timestamp (see AuthService.handleResponsePCA/B2C), so
// no JWT decoding is needed here.
export function isTokenExpiredOrExpiringSoon(bufferMs = 30000): boolean {
  if (typeof window === 'undefined') return false;
  const raw = localStorage.getItem('userinfo');
  if (!raw) return false;
  try {
    const info: StoredUserInfo = JSON.parse(raw);
    if (!info.token || !info.exp) return false;
    return info.exp <= Date.now() + bufferMs;
  } catch {
    return false;
  }
}

function notifySubscribers(token: string): void {
  subscribers.forEach((s) => s.resolve(token));
  subscribers = [];
}

function rejectSubscribers(err: unknown): void {
  subscribers.forEach((s) => s.reject(err));
  subscribers = [];
}

function fireTokenExpired(): void {
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new CustomEvent('token-expired'));
  }
}

function delay(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

const CROSS_TAB_LOCK_NAME = 'msal-token-refresh';

// Serializes fn across browser tabs via the Web Locks API when available
// (all evergreen browsers; Safari 15.4+), so only one tab at a time can be
// mid-acquireTokenSilent — this is what actually prevents two tabs from
// racing Azure AD's refresh-token rotation (rotation invalidates the losing
// tab's refresh token even though the session itself is still valid).
// Falls back to calling fn directly when navigator.locks isn't available.
function withCrossTabLock<T>(fn: () => Promise<T>): Promise<T> {
  const locks = typeof navigator !== 'undefined' ? navigator.locks : undefined;
  if (!locks) return fn();
  try {
    return locks.request(
      CROSS_TAB_LOCK_NAME,
      { signal: AbortSignal.timeout(CROSS_TAB_LOCK_TIMEOUT_MS) },
      () => fn(),
    );
  } catch {
    // AbortSignal.timeout unsupported (very old browser) - fall back to unbounded lock.
    return locks.request(CROSS_TAB_LOCK_NAME, () => fn());
  }
}

// A losing tab in the cross-tab race above needs a brief window for the
// winning tab's rotation to finish writing MSAL's updated cache to
// localStorage before its own retry can succeed — these delays are tuned to
// that, not to wait out a genuinely dead session.
const RETRY_DELAYS_MS = [400, 800];

// Bounds on the operations that can otherwise hang indefinitely: a stuck
// acquireTokenSilent() call, a cross-tab lock held by a stalled sibling tab,
// or a queued subscriber waiting on an owner that never settles. Without
// these, concurrent callers (e.g. several API calls fired right after login)
// can wait forever instead of failing cleanly.
const ACQUIRE_TOKEN_TIMEOUT_MS = 15_000;
const CROSS_TAB_LOCK_TIMEOUT_MS = 20_000;
const SUBSCRIBER_TIMEOUT_MS = 20_000;
// Bounds the owner's ENTIRE retry loop (not just a single attempt), so the
// owner's worst-case wait stays roughly aligned with SUBSCRIBER_TIMEOUT_MS
// instead of accumulating to attempts * ACQUIRE_TOKEN_TIMEOUT_MS + delays.
const OWNER_TOTAL_TIMEOUT_MS = 20_000;

function withTimeout<T>(promise: Promise<T>, ms: number, message: string): Promise<T> {
  return new Promise<T>((resolve, reject) => {
    const timer = setTimeout(() => reject(new Error(message)), ms);
    promise.then(
      (v) => { clearTimeout(timer); resolve(v); },
      (e) => { clearTimeout(timer); reject(e); },
    );
  });
}

// Mirrors the refreshed token/expiry into localStorage['userinfo'] (the same
// key AuthService.handleResponsePCA/B2C write at login) so that a) sibling
// tabs get a `storage` event to react to, and b) isTokenExpiredOrExpiringSoon
// reflects the refresh instead of staying pinned to the original login exp.
export function persistRefreshedToken(idToken: string, exp: number | undefined): void {
  if (typeof window === 'undefined') return;
  try {
    const raw = localStorage.getItem('userinfo');
    const info: StoredUserInfo = raw ? JSON.parse(raw) : {};
    localStorage.setItem('userinfo', JSON.stringify({ ...info, token: idToken, exp: exp ?? info.exp }));
  } catch {
    // Best-effort — worst case the cross-tab freshness check just won't see this refresh.
  }
}

async function acquireToken(): Promise<string> {
  const result = await withTimeout(
    authService.getTokenSilent(),
    ACQUIRE_TOKEN_TIMEOUT_MS,
    'Timed out waiting for MSAL acquireTokenSilent',
  );
  if (!result) {
    throw new Error('MSAL silent token acquisition failed');
  }
  persistRefreshedToken(result.idToken, result.expiresOn?.getTime());
  return result.idToken;
}

// Coordinates token refresh across concurrent callers so only one
// acquireTokenSilent() call is ever in flight at a time *within this tab*.
// Callers that arrive while a refresh is already running queue here and
// receive the result of the in-flight refresh instead of starting their
// own. Across tabs, the actual acquisition is additionally serialized via
// the Web Locks API (see withCrossTabLock) so two tabs can't race Azure
// AD's refresh-token rotation and invalidate each other. On failure,
// retries a couple of times with a short delay before giving up — a failed
// silent refresh isn't always a genuinely dead session; it can also be a
// losing tab in the cross-tab race (whose retry will likely succeed once
// the winning tab's rotation has settled) or a transient network blip.
export async function attemptTokenRefresh(): Promise<string> {
  if (isRefreshing) {
    return new Promise<string>((resolve, reject) => {
      const subscriber: Subscriber & { settled?: boolean } = {
        resolve: (token) => { subscriber.settled = true; resolve(token); },
        reject: (err) => { subscriber.settled = true; reject(err); },
      };
      subscribers.push(subscriber);
      setTimeout(() => {
        if (subscriber.settled) return;
        const idx = subscribers.indexOf(subscriber);
        if (idx !== -1) subscribers.splice(idx, 1);
        subscriber.settled = true;
        reject(new Error('Timed out waiting for in-flight token refresh'));
      }, SUBSCRIBER_TIMEOUT_MS);
    });
  }

  isRefreshing = true;

  try {
    let lastErr: unknown;
    try {
      const idToken = await withTimeout(
        (async () => {
          for (let attempt = 0; attempt <= RETRY_DELAYS_MS.length; attempt++) {
            if (attempt > 0) await delay(RETRY_DELAYS_MS[attempt - 1]);
            try {
              return await withCrossTabLock(acquireToken);
            } catch (err) {
              lastErr = err;
            }
          }
          throw lastErr;
        })(),
        OWNER_TOTAL_TIMEOUT_MS,
        'Timed out waiting for token refresh',
      );
      isRefreshing = false;
      notifySubscribers(idToken);
      return idToken;
    } catch (err) {
      lastErr = err;
    }
    // All retries exhausted from this tab's own acquireTokenSilent calls —
    // but a sibling tab may have won the cross-tab race and already written
    // a fresh token to localStorage while we were retrying. Trust that over
    // giving up, since the thing we actually care about is a valid stored
    // token, not that *this* tab's own MSAL call succeeded.
    if (!isTokenExpiredOrExpiringSoon()) {
      try {
        const info: StoredUserInfo = JSON.parse(localStorage.getItem('userinfo') ?? '{}');
        if (info.token) {
          isRefreshing = false;
          notifySubscribers(info.token);
          return info.token;
        }
      } catch {
        // fall through to throw below
      }
    }
    throw lastErr;
  } catch (err) {
    isRefreshing = false;
    fireTokenExpired();
    rejectSubscribers(err);
    throw err;
  }
}
